import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "../index";
import type { OptionalSystem } from "../../types/optionalSystems";
import type { CommonAreaSystem } from "../../types/optionalSystems";

// Select all optional systems from the optionalSystems slice
const selectOptionalSystems = (state: RootState) =>
  state.optionalSystems.systems;

const selectPowerPlants = createSelector(selectOptionalSystems, (systems) =>
  systems.filter((system: OptionalSystem) => system.type === "powerPlant"),
);

const selectSoftwareSystems = createSelector(selectOptionalSystems, (systems) =>
  systems.filter((system: OptionalSystem) => system.type === "software"),
);

// Select all optional systems from all categories
export const selectAllOptionalSystems = createSelector(
  [
    (state: RootState) => state.optionalSystems.structure,
    (state: RootState) => state.optionalSystems.modularHull,
    (state: RootState) => state.optionalSystems.power,
    (state: RootState) => state.optionalSystems.drives,
    (state: RootState) => state.optionalSystems.fuel,
    (state: RootState) => state.optionalSystems.accommodations,
    (state: RootState) => state.optionalSystems.bridge,
    (state: RootState) => state.optionalSystems.cargo,
    (state: RootState) => state.optionalSystems.drones,
    (state: RootState) => state.optionalSystems.sensors,
    (state: RootState) => state.optionalSystems.external,
    (state: RootState) => state.optionalSystems.internal,
    (state: RootState) => state.optionalSystems.software,
    (state: RootState) => state.optionalSystems.commonAreas,
  ],
  (
    structure,
    modularHull,
    power,
    drives,
    fuel,
    accommodations,
    bridge,
    cargo,
    drones,
    sensors,
    external,
    internal,
    software,
    commonAreas,
  ) => [
    ...(Array.isArray(structure) ? structure : []),
    ...(Array.isArray(modularHull) ? modularHull : []),
    ...(Array.isArray(power) ? power : []),
    ...(Array.isArray(drives) ? drives : []),
    ...(Array.isArray(fuel) ? fuel : []),
    ...(Array.isArray(accommodations) ? accommodations : []),
    ...(Array.isArray(bridge) ? bridge : []),
    ...(Array.isArray(cargo) ? cargo : []),
    ...(Array.isArray(drones) ? drones : []),
    ...(Array.isArray(sensors) ? sensors : []),
    ...(Array.isArray(external) ? external : []),
    ...(Array.isArray(internal) ? internal : []),
    ...(Array.isArray(software) ? software : []),
    ...(Array.isArray(commonAreas) ? commonAreas : []),
  ],
);

// Select the first (or only) Modular Hull configuration
export const selectModularHullConfig = (state: RootState) =>
  state.optionalSystems.modularHull.length > 0
    ? state.optionalSystems.modularHull[0]
    : undefined;

// Derived selectors
const selectTotalPowerOutput = createSelector(
  selectPowerPlants,
  (powerPlants) =>
    (powerPlants || []).reduce((total: number, plant: OptionalSystem) => {
      if (plant.type === "powerPlant" && "basePower" in plant.properties) {
        return total + ((plant.properties.basePower as number) || 0);
      }
      return total;
    }, 0),
);

const selectTotalSoftwareCost = createSelector(
  selectSoftwareSystems,
  (software) =>
    (software || []).reduce(
      (total: number, system: OptionalSystem) => total + system.cost,
      0,
    ),
);

const selectTotalOptionalSystemsCost = createSelector(
  selectAllOptionalSystems,
  (systems) =>
    (systems || []).reduce(
      (total: number, system: OptionalSystem) => total + system.cost,
      0,
    ),
);

const selectTotalOptionalSystemsPower = createSelector(
  selectAllOptionalSystems,
  (systems) =>
    (systems || []).reduce((total: number, system: OptionalSystem) => {
      if (system.type === "modularHull" && Array.isArray(system.modules)) {
        // Only count fit-out power of installed modules
        const fitoutPower = (system.modules || [])
          .filter((m) => m.installed)
          .reduce((sum, m) => sum + (m.fitoutPower || 0), 0);
        return total + fitoutPower;
      }
      // For all other systems, count power as before
      return total + (system.power || 0);
    }, 0),
);

const selectTotalOptionalSystemsTonnage = createSelector(
  selectAllOptionalSystems,
  (systems) =>
    (systems || []).reduce((total: number, system: OptionalSystem) => {
      // External cargo mounts don't consume internal space
      if (system.type === "cargo" && system.subtype === "externalCargoMount") {
        return total;
      }

      if (system.type === "modularHull" && Array.isArray(system.modules)) {
        // Only count fit-out weight of installed modules
        const fitoutWeight = (system.modules || [])
          .filter((m) => m.installed)
          .reduce((sum, m) => sum + (m.fitoutWeight || 0), 0);
        return total + fitoutWeight;
      }
      // For all other systems, count tonnage as before
      return total + (system.tonnage || 0);
    }, 0),
);

export const selectCommonAreas = (state: RootState): CommonAreaSystem[] => {
  const commonAreas = state.optionalSystems.commonAreas;
  return Array.isArray(commonAreas) ? commonAreas : [];
};

export const selectors = {
  selectOptionalSystems,
  selectAllOptionalSystems,
  selectPowerPlants,
  selectSoftwareSystems,
  selectTotalPowerOutput,
  selectTotalSoftwareCost,
  selectTotalOptionalSystemsCost,
  selectTotalOptionalSystemsPower,
  selectTotalOptionalSystemsTonnage,
  selectModularHullConfig,
  selectCommonAreas,
} as const;
